import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Button, TextField, Checkbox,
  FormControlLabel, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { api } from '../../service';

const DAY_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

const blank = () => ({
  start_time: '09:00',
  end_time: '13:00',
  weekdays: 0,
  applies_holiday: false,
});

const formatDays = (r) => {
  const parts = [];
  for (let i = 0; i < 7; i++) if ((r.weekdays & (1 << i)) !== 0) parts.push(DAY_LABELS[i]);
  if (r.applies_holiday) parts.push('Feriados');
  return parts.join(', ') || '—';
};

const WasherAvailability = () => {
  const { washerId } = useParams();
  const [ranges, setRanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/scheduling/ranges/${washerId}`);
      setRanges((data?.data?.ranges) || []);
    } catch (e) {
      setError(e?.response?.data?.codeDetail || 'load_failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (washerId) load(); }, [washerId]);

  const toggleDay = (bit) => setEditing({ ...editing, weekdays: editing.weekdays ^ (1 << bit) });
  const isDay = (bit) => ((editing.weekdays || 0) & (1 << bit)) !== 0;

  const save = async () => {
    if (editing.end_time <= editing.start_time) { setError('end_before_start'); return; }
    if (!editing.weekdays && !editing.applies_holiday) { setError('no_days'); return; }
    try {
      await api.post(`/scheduling/ranges/${washerId}`, {
        start_time: editing.start_time,
        end_time: editing.end_time,
        weekdays: editing.weekdays,
        applies_holiday: editing.applies_holiday,
      });
      setEditing(null);
      setSnack('Guardado');
      load();
    } catch (e) {
      setError(e?.response?.data?.codeDetail || 'save_failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Apagar este horário?')) return;
    try {
      await api.delete(`/scheduling/ranges/${id}`);
      setSnack('Apagado');
      load();
    } catch (e) {
      setError(e?.response?.data?.codeDetail || 'delete_failed');
    }
  };

  return (
    <Container maxWidth="md" style={{ paddingTop: 24, paddingBottom: 24 }}>
      <Typography variant="h4" style={{ marginBottom: 16 }}>Disponibilidade do Washer</Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Define os horários em que este washer aceita lavagens agendadas. Cada
        linha é um intervalo de tempo aplicável a um conjunto de dias da semana
        e/ou feriados.
      </Typography>

      {error && <Box mb={2}><Typography color="error">⚠️ {error}</Typography></Box>}

      <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
        {loading && <Typography>Loading…</Typography>}
        {!loading && ranges.length === 0 && (
          <Typography color="textSecondary">Sem horários configurados.</Typography>
        )}
        {ranges.map((r) => (
          <Box key={r.id} display="flex" justifyContent="space-between" alignItems="center"
               style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
            <div>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                {r.start_time} – {r.end_time}
              </Typography>
              <Typography variant="body2" color="textSecondary">{formatDays(r)}</Typography>
            </div>
            <IconButton onClick={() => remove(r.id)}><DeleteIcon /></IconButton>
          </Box>
        ))}

        <Box mt={2}>
          <Button variant="contained" color="primary" startIcon={<AddIcon />}
                  onClick={() => setEditing(blank())}>
            Adicionar horário
          </Button>
        </Box>
      </Paper>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo horário</DialogTitle>
        <DialogContent>
          {editing && (
            <Box display="flex" flexDirection="column" gridGap={12}>
              <TextField label="Início" type="time" inputProps={{ step: 900 }}
                value={editing.start_time}
                onChange={(e) => setEditing({ ...editing, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="Fim" type="time" inputProps={{ step: 900 }}
                value={editing.end_time}
                onChange={(e) => setEditing({ ...editing, end_time: e.target.value })}
                InputLabelProps={{ shrink: true }} fullWidth />
              <Typography variant="caption">Dias</Typography>
              <Box display="flex" flexWrap="wrap" gridGap={6}>
                {DAY_LABELS.map((lbl, i) => (
                  <Button key={i} variant={isDay(i) ? 'contained' : 'outlined'}
                    color={isDay(i) ? 'primary' : 'default'} onClick={() => toggleDay(i)}>
                    {lbl}
                  </Button>
                ))}
              </Box>
              <FormControlLabel
                control={<Checkbox checked={editing.applies_holiday}
                  onChange={(e) => setEditing({ ...editing, applies_holiday: e.target.checked })} />}
                label="Aplica-se em feriados nacionais"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={save}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')} message={snack} />
    </Container>
  );
};

export default WasherAvailability;
